/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Image } from "react-native";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../firebase.config";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import SignaturePad from "./SignaturePad";
import SignatureCaptureModal from "./SignatureCaptureModal";
import GoogleAddressInput from "./GoogleAddressInput";
import jsPDF from "jspdf";
import { getCollectionPath, getSettingsPath } from "../utils/dbPaths";

const ThirdPartyWaiver = ({ legalStoreName, legalStoreAddress, bookingId, transferEmail }) => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Waiver form data
  const [typedName, setTypedName] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [fullSignature, setFullSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // Phone breakpoint; the inline pad is fine with a mouse but cramped with a finger.
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isPhone = viewportWidth < 768;
  const [agreed, setAgreed] = useState(false);

  // Load transfer waiver template from Firestore
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        console.log("📋 Loading transfer waiver template from Firestore...");
        const settingsPath = getSettingsPath();
        const templateRef = doc(db, settingsPath, "transferWaiverTemplate");
        const templateDoc = await getDoc(templateRef);
        
        let templateData;
        
        if (!templateDoc.exists()) {
          console.log("⚠️ No transfer waiver template found, creating default template...");
          
          // Create default waiver template
          const defaultTemplate = {
            sections: [
              {
                id: "assumption-of-risk",
                title: "ASSUMPTION OF RISK",
                content: "I understand that renting and using equipment from <storeName> involves inherent risks including, but not limited to, accidents, injuries, property damage, or equipment malfunction. I voluntarily assume all such risks associated with my use of the rented equipment.",
                requiresInitial: true,
                order: 1,
                visible: true
              },
              {
                id: "liability-waiver",
                title: "RELEASE OF LIABILITY",
                content: "In consideration of being permitted to rent equipment from <storeName>, I hereby release, waive, and discharge <storeName>, its owners, employees, agents, and representatives from any and all liability, claims, demands, or causes of action arising from personal injury, property damage, or any other loss sustained while using the rented equipment, whether caused by negligence or otherwise.",
                requiresInitial: true,
                order: 2,
                visible: true
              },
              {
                id: "equipment-responsibility",
                title: "EQUIPMENT CARE AND RESPONSIBILITY",
                content: "I acknowledge that I am responsible for the rented equipment from the time of pickup until its return to <storeName>. I agree to use the equipment in a safe and responsible manner and will be liable for any damage, loss, or theft of the equipment during the rental period. I will return the equipment in the same condition as received, normal wear and tear excepted.",
                requiresInitial: true,
                order: 3,
                visible: true
              },
              {
                id: "indemnification",
                title: "INDEMNIFICATION",
                content: "I agree to indemnify, defend, and hold harmless <storeName> from and against any and all claims, liabilities, damages, losses, or expenses (including attorney's fees) arising out of or resulting from my use of the rented equipment or any actions taken by me during the rental period.",
                requiresInitial: false,
                order: 4,
                visible: true
              }
            ],
            finalSignatureSection: {
              title: "FULL SIGNATURE",
              content: "By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions contained in this Liability Waiver and Release. I understand that I am giving up substantial rights, including my right to sue. I confirm that I am at least 18 years of age and am legally competent to sign this agreement.",
              requiresTypedName: true,
              requiresHomeAddress: true,
              requiresSignature: true
            },
            lastUpdated: new Date().toISOString()
          };
          
          // Save default template to Firestore
          await setDoc(templateRef, defaultTemplate);
          console.log("✅ Default transfer waiver template created");
          templateData = defaultTemplate;
        } else {
          templateData = templateDoc.data();
          console.log("✅ Transfer waiver template loaded:", templateData);
        }
        
        setTemplate(templateData);
        
      } catch (error) {
        console.error("❌ Error loading waiver template:", error);
        alert("Error: Could not load waiver template. " + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplate();
  }, []);

  // Replace variables in text
  const replaceVariables = (text) => {
    if (!text) return "";
    return text
      .replace(/<storeName>/g, legalStoreName)
      .replace(/<storeAddress>/g, legalStoreAddress)
      .replace(/<customerName>/g, transferEmail) // For transfer waivers, use email as identifier
      .replace(/<date>/g, new Date().toLocaleDateString());
  };

  const generateWaiverPDF = async () => {
    try {
      console.log("📄 Generating Transfer Waiver PDF from template...");
      
      const pdfDoc = new jsPDF();
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 20;

      // Title
      pdfDoc.setFontSize(16);
      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text("TRANSFER WAIVER AND RELEASE", pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 7;

      pdfDoc.setFontSize(12);
      pdfDoc.text(
        `${legalStoreName} - Equipment Rental Agreement`,
        pageWidth / 2,
        yPos,
        { align: "center" }
      );
      yPos += 7;
      
      if (legalStoreAddress && legalStoreAddress.trim()) {
        pdfDoc.setFontSize(10);
        pdfDoc.setFont(undefined, "normal");
        pdfDoc.text(legalStoreAddress, pageWidth / 2, yPos, { align: "center" });
        yPos += 10;
      } else {
        yPos += 5;
      }

      // Participant Information
      pdfDoc.setFontSize(11);
      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text("PARTICIPANT INFORMATION", margin, yPos);
      yPos += 7;

      pdfDoc.setFont(undefined, "normal");
      pdfDoc.text(`Participant Name: ${typedName}`, margin, yPos);
      yPos += 7;
      pdfDoc.text(`Email: ${transferEmail}`, margin, yPos);
      yPos += 7;
      pdfDoc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos);
      yPos += 7;
      pdfDoc.text(`Home Address: ${homeAddress || "Not provided"}`, margin, yPos);
      yPos += 12;

      // Render sections from template
      const visibleSections = template.sections.filter(s => s.visible);
      
      for (let i = 0; i < visibleSections.length; i++) {
        const section = visibleSections[i];
        const sectionContent = replaceVariables(section.content);
        
        // Check if we need a new page
        if (yPos > 250) {
          pdfDoc.addPage();
          yPos = 20;
        }

        pdfDoc.setFont(undefined, "bold");
        pdfDoc.text(`${section.order}. ${section.title}`, margin, yPos);
        yPos += 7;
        
        pdfDoc.setFont(undefined, "normal");
        const contentLines = pdfDoc.splitTextToSize(sectionContent, pageWidth - 2 * margin);
        pdfDoc.text(contentLines, margin, yPos);
        yPos += contentLines.length * 5 + 5;

      }

      // Final signature section
      if (yPos > 230) {
        pdfDoc.addPage();
        yPos = 20;
      }

      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text(template.finalSignatureSection.title, margin, yPos);
      yPos += 7;
      
      pdfDoc.setFont(undefined, "normal");
      const finalContent = replaceVariables(template.finalSignatureSection.content);
      const finalLines = pdfDoc.splitTextToSize(finalContent, pageWidth - 2 * margin);
      pdfDoc.text(finalLines, margin, yPos);
      yPos += finalLines.length * 5 + 10;

      if (template.finalSignatureSection.requiresTypedName) {
        pdfDoc.text(`Typed Full Name: ${typedName}`, margin, yPos);
        yPos += 10;
      }

      if (template.finalSignatureSection.requiresSignature && fullSignature) {
        pdfDoc.text("Signature:", margin, yPos);
        yPos += 5;
        pdfDoc.addImage(fullSignature, "PNG", margin, yPos, 60, 20);
        yPos += 25;
      }

      pdfDoc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos);

      const pdfBlob = pdfDoc.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  const handleSignWaiver = async () => {
    console.log("🔵 handleSignWaiver called for transfer waiver!");

    const nameValid = !template?.finalSignatureSection?.requiresTypedName || typedName.trim();
    const addressValid = !template?.finalSignatureSection?.requiresHomeAddress || homeAddress.trim();
    const signatureValid = !template?.finalSignatureSection?.requiresSignature || fullSignature;
    
    return nameValid && addressValid && signatureValid && agreed;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading waiver...</Text>
      </View>
    );
  }

  if (!template) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>❌ Waiver template not configured</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }} 
          style={styles.cancelButton} 
          onPress={() => navigate(`/booking/${bookingId}`)}
        >
          <Text style={styles.cancelButtonText}>Go to Booking</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const visibleSections = template.sections.filter(s => s.visible);

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>TRANSFER WAIVER AND RELEASE</Text>
      <Text style={styles.subtitle}>
        {legalStoreName} - Equipment Rental Agreement
      </Text>
      {legalStoreAddress && (
        <Text style={[styles.subtitle, { fontSize: 12, marginTop: 4, color: "#5C6B68" }]}>
          {legalStoreAddress}
        </Text>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>PARTICIPANT INFORMATION</Text>
        <Text style={styles.text}>
          Transfer Email: {transferEmail || "[Email will be filled automatically]"}
        </Text>
        <Text style={styles.text}>Date: {new Date().toLocaleDateString()}</Text>
      </View>

      {/* Dynamic sections from template */}
      {visibleSections.map((section) => (
        <View key={section.id} style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionNumber}>{section.order}</Text>
            <Text style={styles.sectionTitle}>{section.title}</Text>
          </View>
          <Text style={styles.text}>
            {replaceVariables(section.content)}
          </Text>
        </View>
      ))}
        
      {/* Final Signature Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{template.finalSignatureSection.title}</Text>
        <Text style={styles.text}>
          {replaceVariables(template.finalSignatureSection.content)}
        </Text>

        {template.finalSignatureSection.requiresTypedName && (
          <View style={styles.nameInputContainer}>
            <Text style={styles.nameInputLabel}>
              Type Your Full Name <Text style={styles.requiredAsterisk}>*</Text>
        </Text>
            <TextInput
              style={styles.nameInput}
              placeholder="Enter your full legal name"
              placeholderTextColor="#AEB8B6"
              value={typedName}
              // autoCapitalize only drives virtual keyboards; capitalise here so the
              // field is not left lowercase on desktop.
              onChangeText={(text) =>
                setTypedName(text.replace(/(^|[\s'-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase()))
              }
              autoCapitalize="words"
            />
      </View>
        )}

        {template.finalSignatureSection.requiresHomeAddress && (
          <View style={styles.nameInputContainer}>
            <Text style={styles.nameInputLabel}>
              Home Address <Text style={styles.requiredAsterisk}>*</Text>
        </Text>
            <GoogleAddressInput
              value={homeAddress}
              onChangeText={setHomeAddress}
              placeholder="Start typing your home address..."
              multiline
            />
            <Text style={styles.addressHelperText}>
              User must click on address to continue to payment
            </Text>
      </View>
        )}

        {template.finalSignatureSection.requiresSignature && (
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>
              Full Signature: <Text style={styles.requiredAsterisk}>*</Text>
        </Text>
            {isPhone ? (
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={[styles.signTapButton, !fullSignature && styles.signTapButtonRequired]}
                onPress={() => setShowSignatureModal(true)}
              >
                {fullSignature ? (
                  <Image
                    source={{ uri: fullSignature }}
                    style={styles.signaturePreview}
                    resizeMode="contain"
                  />
                ) : (
                  <Text style={styles.signTapText}>✍️  Tap to sign</Text>
                )}
              </TouchableOpacity>
            ) : (
              <SignaturePad
                fullWidth
                height={360}
                isEmpty={!fullSignature}
                onSignatureChange={setFullSignature}
              />
            )}
            {isPhone && fullSignature && (
              <Text style={styles.signRetakeHint}>Tap your signature to redo it</Text>
            )}
      </View>
        )}
      </View>

      <View style={styles.agreementRow}>
        <TouchableOpacity
          style={[styles.checkbox, agreed && styles.checkboxChecked]}
          onPress={() => setAgreed(!agreed)}
        >
          {agreed && <Text style={styles.checkmark}>✓</Text>}
        </TouchableOpacity>
        <Text style={styles.agreementText}>
          I have read and understand this waiver. I understand that by signing
          this waiver, I am giving up legal rights and remedies that may be
          available to me. I voluntarily agree to be bound by all terms and
          conditions.
        </Text>
      </View>

      <View style={styles.buttonContainer}>
        <TouchableOpacity dataSet={{ hover: "lift" }} 
          style={styles.cancelButton} 
          onPress={() => navigate(`/booking/${bookingId}`)}
        >
          <Text style={styles.cancelButtonText}>View Booking</Text>
        </TouchableOpacity>
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[
            styles.signButton,
            (!canSign() || generating) && styles.signButtonDisabled,
          ]}
          onPress={handleSignWaiver}
          disabled={!canSign() || generating}
        >
          {generating ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.signButtonText}>Sign & Submit Waiver</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Full-screen landscape capture, phones only. Kept outside the inline pad
          branch so it survives re-renders while open. */}
      <SignatureCaptureModal
        visible={showSignatureModal}
        title="Sign your name"
        onCancel={() => setShowSignatureModal(false)}
        onConfirm={(sig) => {
          setShowSignatureModal(false);
          setFullSignature(sig);
          
        }}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: '#5C6B68',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    color: '#C4453D',
    marginBottom: 20,
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
    color: "#0F1A19",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 20,
    color: "#5C6B68",
  },
  section: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E3F2EF",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  sectionNumber: {
    fontSize: 12,
    fontWeight: "700",
    color: "#FFFFFF",
    backgroundColor: "#0E7C6B",
    width: 22,
    height: 22,
    borderRadius: 11,
    textAlign: "center",
    lineHeight: 22,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#0F1A19",
  },
  text: {
    fontSize: 14,
    lineHeight: 20,
    color: "#2E3A38",
    marginBottom: 12,
  },
  signatureBox: {
    marginTop: 16,
    marginBottom: 8,
  },
  signatureLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 8,
    color: "#5C6B68",
  },
  requiredAsterisk: {
    color: "#C4453D",
    fontSize: 12,
  },
  instructionText: {
    fontSize: 11,
    color: "#8A9794",
    marginTop: 8,
    textAlign: "center",
    fontStyle: "italic",
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  // A real row: the text used to be pulled up beside the checkbox with a negative
  // margin, which clipped once the shared `section` style gained overflow:hidden.
  agreementRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    padding: 16,
  },
  signTapButton: {
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderStyle: "dashed",
    borderRadius: 8,
    minHeight: 120,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
  },
  signTapButtonRequired: { borderColor: "#C4453D", backgroundColor: "#FBEAE9" },
  signTapText: { fontSize: 16, fontWeight: "600", color: "#0E7C6B" },
  signaturePreview: { width: "100%", height: 120 },
  signRetakeHint: { fontSize: 12, color: "#8A9794", marginTop: 6, textAlign: "center" },
  agreementText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: "#2E3A38",
    marginLeft: 12,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    marginBottom: 20,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: "#DDE3E2",
    borderRadius: 6,
    marginRight: 10,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
  },
  signButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    marginLeft: 10,
    alignItems: "center",
  },
  signButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  signButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  nameInputContainer: {
    marginBottom: 16,
    marginTop: 12,
  },
  nameInputLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 8,
    color: "#5C6B68",
  },
  nameInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  addressHelperText: {
    fontSize: 11,
    color: "#B4761B",
    marginTop: 4,
    fontStyle: "italic",
  },
});

export default ThirdPartyWaiver;
